import React, { type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import cn from 'classnames';

import { BACKGROUNDS_CONFIG } from '@/common/config/media-config';
import { playSound } from '@/common/lib/soundEffect';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { type ContractDataType } from '@/common/types/contract';
import { type User } from '@/common/types/userType';
import { ContractRewardMotion } from '@/components/Animations/ContractRewardMotion/ContractRewardMotion';
import { ContractItem } from '@/components/ContractItem/ContractItem';
import { H1, H3, StringSmall } from '@/components/Typography/Typography';
import { ModalCharacterBackground } from '@/components/ui/Backgrounds/ModaCharacterBackground/ModalCharacterBackground';
import { MainButton, SecondaryButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { MotionPicture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import css from './styles.module.scss';

import cashPng from '/img/icons/cash-large.png';
import cashWebp from '/img/icons/cash-large.webp';
import starPng from '/img/icons/star-large.png';
import starWebp from '/img/icons/star-large.webp';

interface DefaultProps {
  closeModal: () => void;
  contractData: ContractDataType;
  isAnimationStart: boolean;
  completeContractApiState: { isLoading: boolean };
  rejectContractApiState: { isLoading: boolean };
  hasContractCompleted: boolean;
  onComplete: () => void;
  withOneItem: boolean;
  setIsCancelConfirmOpen: Dispatch<SetStateAction<boolean>>;
  isCancelConfirmOpen: boolean;
  user: User | null;
  onReject: () => void;
  statusVIP?: boolean;
}

const Default: React.FC<DefaultProps> = ({
  closeModal,
  contractData,
  isAnimationStart,
  completeContractApiState,
  rejectContractApiState,
  hasContractCompleted,
  onComplete,
  withOneItem,
  setIsCancelConfirmOpen,
  isCancelConfirmOpen,
  user,
  onReject,
  statusVIP,
}) => {
  const { t } = useTranslation();
  const ref = useHighlightComponentRect<HTMLButtonElement>(25);
  const productsOfDay = useAppSelector((state) => state.contract.productOfDay);
  const foundProductOfDay =
    productsOfDay?.filter((product) =>
      Object.keys(contractData.data).includes(product.name),
    ) ?? [];

  const extraReward =
    foundProductOfDay?.length > 0
      ? foundProductOfDay
          .reduce((acc, product) => acc + product.extraStarPoints, 0)
          .toString()
      : undefined;

  return (
    <div className="relative w-full z-0">
      <Close
        onClick={closeModal}
        className="absolute right-4 top-4 z-[9]"
        imageVariant="modal"
        variant="modal"
      />
      <div className="absolute top-0 left-0 z-[-1]">
        <ModalCharacterBackground
          classNames={{
            wrapper: css.bgWrapper,
          }}
          backgroundImage={{
            png: BACKGROUNDS_CONFIG.BG_CONTRACT.png,
            webp: BACKGROUNDS_CONFIG.BG_CONTRACT.webp,
          }}
        />
      </div>
      <div className="w-full h-full justify-between flex flex-col items-center gap-6 min-h-[33.375rem]">
        <H1
          className={withOneItem ? 'mt-7 mb-2' : 'mt-7 mb-2'}
          weight="bold"
          style={{ position: 'relative' }}
        >
          {t('Contract')}
        </H1>
        <div
          className={cn(
            withOneItem ? 'h-[11.5rem] mb-[0.625rem]' : 'h-[11.5rem] mb-2',
          )}
        >
          <ContractItem {...contractData} inModal={true} />
        </div>
        <div
          className={cn(
            'flex flex-col items-center gap-2',
            isCancelConfirmOpen && 'gap-3 flex-1 justify-center pt-1',
          )}
        >
          <H3
            weight="bold"
            className={cn(
              '!text-white',
              isCancelConfirmOpen && 'text-[1.25rem]',
            )}
          >
            {t(isCancelConfirmOpen ? 'Removing' : 'Reward')}
          </H3>
          <StringSmall
            weight="regular"
            className={cn(
              '!text-white/60',
              isCancelConfirmOpen && 'max-w-[20rem] text-center',
            )}
          >
            {isCancelConfirmOpen
              ? `${t('Are you sure you want to remove this contract?')}
                ${t('A new one will appeare in {{time}} minutes', {
                  time: user?.settings.contractRejectionLifetime,
                })}`
              : t('Complete the contract and get your reward')}
          </StringSmall>
        </div>
        {!isCancelConfirmOpen && (
          <div className="flex -mt-1">
            {!isAnimationStart && (
              <>
                <ContractRewardMotion
                  motionProps={{
                    layoutId: 'cash-reward',
                    transition: { duration: 0.5, ease: 'easeOut' },
                    onLayoutAnimationComplete: () => {
                      console.log('layout animation complete');
                    },
                  }}
                  title={toShortNumFormat(contractData.cash)}
                  extraText={
                    statusVIP
                      ? toShortNumFormat((contractData.cash * 10) / 100)
                      : undefined
                  }
                  description={t('Cash')}
                  classNames={{
                    title: '!text-xl !leading-none',
                    description: '!text-xs !leading-none',
                    wrapper: '!w-[7.5rem]',
                  }}
                >
                  <div className="w-12 h-12 shrink-0 relative">
                    <MotionPicture
                      className="w-12 h-12 shrink-0"
                      img={cashPng}
                      webp={cashWebp}
                      alt="cash"
                    />
                    {!!statusVIP && (
                      <div className="absolute top-0 right-[-30%]">
                        <img src="/img/VIP/vip-icon.png" alt="" width={24} />
                      </div>
                    )}
                  </div>
                </ContractRewardMotion>
                <ContractRewardMotion
                  motionProps={{
                    layoutId: 'point-reward',
                    transition: { duration: 0.5, ease: 'easeOut' },
                  }}
                  title={contractData.point}
                  description={t('Star Points')}
                  classNames={{
                    title: '!text-xl !leading-none !text-gold',
                    description: '!text-xs !leading-none',
                    wrapper: '!w-[7.5rem]',
                  }}
                  extraText={extraReward}
                >
                  <MotionPicture
                    className="w-12 h-12 shrink-0"
                    img={starPng}
                    webp={starWebp}
                    alt="point"
                  />
                </ContractRewardMotion>
                {!!contractData.bingoBalls && (
                  <ContractRewardMotion
                    motionProps={{
                      layoutId: 'bingo-balls-reward',
                      transition: { duration: 0.5, ease: 'easeOut' },
                    }}
                    title={contractData.bingoBalls}
                    description={t('Bingo Balls')}
                    classNames={{
                      title: '!text-xl !leading-none',
                      description: '!text-xs !leading-none',
                      wrapper: '!w-[7.5rem]',
                    }}
                  >
                    <img
                      src="/img/bingo/bingo-icon.png"
                      alt=""
                      className="w-12 h-12 shrink-0 object-contain"
                    />
                  </ContractRewardMotion>
                )}
              </>
            )}
          </div>
        )}
        <div className="w-full flex gap-2 px-4 mb-3 mt-auto">
          <SecondaryButton
            disabled={
              completeContractApiState.isLoading ||
              rejectContractApiState.isLoading
            }
            inactive={rejectContractApiState.isLoading}
            onClick={() => {
              playSound('buttons/switch');
              setIsCancelConfirmOpen((state) => !state);
            }}
            text={t(isCancelConfirmOpen ? 'Cancel' : 'Remove')}
          />
          <MainButton
            id="intro-step-25"
            ref={ref}
            isLoading={
              isCancelConfirmOpen
                ? rejectContractApiState.isLoading
                : completeContractApiState.isLoading
            }
            disabled={
              isCancelConfirmOpen
                ? rejectContractApiState.isLoading
                : completeContractApiState.isLoading ||
                  rejectContractApiState.isLoading ||
                  !hasContractCompleted
            }
            onClick={isCancelConfirmOpen ? onReject : onComplete}
            inactive={isCancelConfirmOpen ? false : !hasContractCompleted}
            text={t(isCancelConfirmOpen ? 'Remove' : 'Complete')}
          ></MainButton>
        </div>
      </div>
    </div>
  );
};

export default Default;
